/**
 * @file Transfer 穿梭框
 * @description 左右两栏多选迁移；适合权限、角色批量分配。
 *
 * @whenToUse 批量从候选集挑选到已选集合。
 * @whenNotToUse 少量多选下拉（用 MultiSelect）；树勾选（用 Tree checkable）。
 */

import { useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/cn.ts'
import { Button } from '@/components/ui/button.tsx'
import { Checkbox } from '@/components/ui/checkbox.tsx'
import { Input } from '@/components/ui/input.tsx'
import { ScrollArea } from '@/components/ui/scroll-area.tsx'

export type TransferItem = {
  key: string
  title: string
  disabled?: boolean
}

export type TransferProps = {
  dataSource: TransferItem[]
  /** 右侧（目标）keys */
  targetKeys?: string[]
  onChange?: (targetKeys: string[]) => void
  /** 左右标题 */
  titles?: [string, string]
  className?: string
  /** 是否显示搜索 */
  showSearch?: boolean
}

function Panel({
  title,
  items,
  checkedKeys,
  onCheckChange,
  onCheckAll,
  filter,
  onFilterChange,
  showSearch,
}: {
  title: string
  items: TransferItem[]
  checkedKeys: string[]
  onCheckChange: (key: string, checked: boolean) => void
  onCheckAll: (checked: boolean) => void
  filter: string
  onFilterChange: (value: string) => void
  showSearch: boolean
}) {
  const enabled = items.filter((item) => !item.disabled)
  const allChecked =
    enabled.length > 0 && enabled.every((item) => checkedKeys.includes(item.key))
  const someChecked = checkedKeys.length > 0 && !allChecked

  return (
    <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-border bg-raised">
      <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
        <Checkbox
          checked={allChecked}
          indeterminate={someChecked}
          aria-label={`${title}全选`}
          onChange={(event) => onCheckAll(event.target.checked)}
          label={
            <span className="text-sm font-medium text-foreground">
              {title}
              <span className="ml-1 text-xs font-normal text-muted">
                {checkedKeys.length}/{items.length}
              </span>
            </span>
          }
        />
      </div>
      {showSearch ? (
        <div className="border-b border-border p-2">
          <Input
            value={filter}
            placeholder="搜索"
            onChange={(event) => onFilterChange(event.target.value)}
          />
        </div>
      ) : null}
      <ScrollArea className="h-56">
        <div className="flex flex-col gap-1 p-2">
          {items.length === 0 ? (
            <p className="px-2 py-6 text-center text-xs text-muted">暂无数据</p>
          ) : (
            items.map((item) => (
              <Checkbox
                key={item.key}
                checked={checkedKeys.includes(item.key)}
                disabled={item.disabled}
                label={item.title}
                className="w-full rounded-md px-2 py-1.5 hover:opacity-90"
                onChange={(event) =>
                  onCheckChange(item.key, event.target.checked)
                }
              />
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  )
}

export function Transfer({
  dataSource,
  targetKeys = [],
  onChange,
  titles = ['候选', '已选'],
  className,
  showSearch = true,
}: TransferProps) {
  const [leftChecked, setLeftChecked] = useState<string[]>([])
  const [rightChecked, setRightChecked] = useState<string[]>([])
  const [leftFilter, setLeftFilter] = useState('')
  const [rightFilter, setRightFilter] = useState('')

  const leftItems = useMemo(() => {
    const q = leftFilter.trim().toLowerCase()
    return dataSource
      .filter((item) => !targetKeys.includes(item.key))
      .filter((item) => !q || item.title.toLowerCase().includes(q))
  }, [dataSource, targetKeys, leftFilter])

  const rightItems = useMemo(() => {
    const q = rightFilter.trim().toLowerCase()
    return dataSource
      .filter((item) => targetKeys.includes(item.key))
      .filter((item) => !q || item.title.toLowerCase().includes(q))
  }, [dataSource, targetKeys, rightFilter])

  const moveRight = () => {
    const keys = leftChecked.filter((key) => {
      const item = dataSource.find((d) => d.key === key)
      return item && !item.disabled
    })
    onChange?.([...new Set([...targetKeys, ...keys])])
    setLeftChecked([])
  }

  const moveLeft = () => {
    const keys = new Set(
      rightChecked.filter((key) => {
        const item = dataSource.find((d) => d.key === key)
        return item && !item.disabled
      }),
    )
    onChange?.(targetKeys.filter((key) => !keys.has(key)))
    setRightChecked([])
  }

  return (
    <div className={cn('flex flex-col gap-3 sm:flex-row sm:items-stretch', className)}>
      <Panel
        title={titles[0]}
        items={leftItems}
        checkedKeys={leftChecked}
        filter={leftFilter}
        onFilterChange={setLeftFilter}
        showSearch={showSearch}
        onCheckChange={(key, checked) => {
          setLeftChecked((prev) =>
            checked ? [...prev, key] : prev.filter((k) => k !== key),
          )
        }}
        onCheckAll={(checked) => {
          setLeftChecked(
            checked
              ? leftItems.filter((i) => !i.disabled).map((i) => i.key)
              : [],
          )
        }}
      />
      <div className="flex shrink-0 flex-row items-center justify-center gap-2 sm:flex-col">
        <Button
          variant="secondary"
          disabled={leftChecked.length === 0}
          aria-label="移到右侧"
          icon={<ChevronRight className="size-4" aria-hidden />}
          onClick={moveRight}
        />
        <Button
          variant="secondary"
          disabled={rightChecked.length === 0}
          aria-label="移到左侧"
          icon={<ChevronLeft className="size-4" aria-hidden />}
          onClick={moveLeft}
        />
      </div>
      <Panel
        title={titles[1]}
        items={rightItems}
        checkedKeys={rightChecked}
        filter={rightFilter}
        onFilterChange={setRightFilter}
        showSearch={showSearch}
        onCheckChange={(key, checked) => {
          setRightChecked((prev) =>
            checked ? [...prev, key] : prev.filter((k) => k !== key),
          )
        }}
        onCheckAll={(checked) => {
          setRightChecked(
            checked
              ? rightItems.filter((i) => !i.disabled).map((i) => i.key)
              : [],
          )
        }}
      />
    </div>
  )
}
